import { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';

/**
 * Admin → Estimation Config page.
 *
 * Edits the JSON file at estimation-config/config.json that feeds the
 * AI estimator (POST /api/ai/estimate-issue). Lets admins tune:
 *   - Team capacity (story points per dev/week, working days/week,
 *     optional per-dev caps).
 *   - Free-text estimation rules (one rule per textarea line).
 *   - Category multipliers (frontend/backend/infra/data).
 *   - Calibration examples — past tickets with estimated vs actual SP.
 *
 * Plus a "Preview prompt" button that calls /api/estimation/preview to
 * render the EXACT text Gemini would see, without spending a Gemini
 * call. This is the primary tuning loop — edit, preview, save.
 */
export default function AdminEstimationConfig() {
  const { oktaAuth } = useOktaAuth() || {};
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  // Preview state — sample ticket plus result of /preview.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [sample, setSample] = useState({
    summary: 'Add export-to-CSV button to the user list page',
    description: 'Users want to download the current filtered user list as CSV from the admin panel. Should respect current filters and include the same columns.',
    issueType: 'Story',
    devCount: 1,
    usingAi: true,
  });
  // Per-row state for the JIRA loader on calibration rows. MUST stay up
  // here at the top of the component — declared below the early-return
  // guards triggers "Rendered more hooks than during the previous render".
  // Shape: { [rowIdx]: 'loading' | 'error:<msg>' }.
  const [loaderState, setLoaderState] = useState({});

  const authHeader = useCallback(async () => {
    let token = '';
    try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [oktaAuth]);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await authHeader();
      const res = await fetch('/api/estimation/config', { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setConfig(body.config);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
      const res = await fetch('/api/estimation/config', {
        method: 'PUT',
        headers,
        body: JSON.stringify({ config }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const hint = body?.hint ? ` (${body.hint})` : '';
        throw new Error(`${body?.error || `HTTP ${res.status}`}${hint}`);
      }
      setConfig(body.config);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewError('');
    setPreview(null);
    try {
      const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
      const res = await fetch('/api/estimation/preview', {
        method: 'POST',
        headers,
        body: JSON.stringify(sample),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setPreview(body);
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading && !config) return <div style={styles.page}><h2>📏 Estimation Config</h2><div>Loading…</div></div>;
  if (error && !config)   return <div style={styles.page}><h2>📏 Estimation Config</h2><div style={styles.error}>⚠️ {error}</div></div>;
  if (!config)            return null;

  // Local helpers that update slices of the config in immutable fashion.
  const updateTeam = (patch) => setConfig(c => ({ ...c, team: { ...c.team, ...patch } }));
  const updateDeveloperRow = (idx, patch) => setConfig(c => ({
    ...c,
    team: {
      ...c.team,
      developers: c.team.developers.map((d, i) => i === idx ? { ...d, ...patch } : d),
    },
  }));
  const addDeveloper = () => setConfig(c => ({
    ...c, team: { ...c.team, developers: [...c.team.developers, { name: '', storyPointsPerWeek: 0 }] },
  }));
  const removeDeveloper = (idx) => setConfig(c => ({
    ...c, team: { ...c.team, developers: c.team.developers.filter((_, i) => i !== idx) },
  }));

  const updateRulesFromTextarea = (text) => setConfig(c => ({
    ...c,
    // Plain textarea — one rule per line. Empty lines stripped server-side anyway.
    rules: text.split('\n').map(s => s.trim()).filter(Boolean),
  }));

  const updateMultiplier = (key, value) => setConfig(c => ({
    ...c, categoryMultipliers: { ...c.categoryMultipliers, [key]: Number(value) || 0 },
  }));

  const updateCalibrationRow = (idx, patch) => setConfig(c => ({
    ...c,
    calibrationExamples: c.calibrationExamples.map((e, i) => i === idx ? { ...e, ...patch } : e),
  }));
  const addCalibration = () => setConfig(c => ({
    ...c, calibrationExamples: [...c.calibrationExamples, { jiraKey: '', summary: '', estimatedSP: 0, actualSP: 0, notes: '' }],
  }));
  const removeCalibration = (idx) => setConfig(c => ({
    ...c, calibrationExamples: c.calibrationExamples.filter((_, i) => i !== idx),
  }));

  // (loaderState is hoisted to the top of the component to keep hook order
  // consistent across renders — see the useState block above.)
  const loadFromJira = async (idx, key) => {
    const normKey = String(key || '').trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9]+-\d+$/.test(normKey)) {
      setLoaderState(s => ({ ...s, [idx]: 'error:Format should be like CP-537' }));
      return;
    }
    setLoaderState(s => ({ ...s, [idx]: 'loading' }));
    try {
      const headers = await authHeader();
      const res = await fetch(`/api/issue/${encodeURIComponent(normKey)}`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body?.error || `HTTP ${res.status}`);
      const fields = body.issue?.fields || {};
      // /api/issue/:key returns description as readable HTML; strip tags for
      // the brief summary preview in this table (we only need a label).
      const fetchedSummary = (fields.summary || '').trim();
      // estimatedSP comes from the storyPoints custom field, which our
      // earlier discovery work surfaces under fields.storyPoints.value.
      const fetchedSP = Number(fields.storyPoints?.value);
      updateCalibrationRow(idx, {
        jiraKey: normKey,
        summary: fetchedSummary || `(${normKey})`,
        // Don't overwrite a manually-entered estimatedSP if JIRA's value is null.
        ...(Number.isFinite(fetchedSP) ? { estimatedSP: fetchedSP } : {}),
      });
      setLoaderState(s => { const n = { ...s }; delete n[idx]; return n; });
    } catch (err) {
      setLoaderState(s => ({ ...s, [idx]: `error:${err.message}` }));
    }
  };

  return (
    <div style={styles.page}>
      {/* Header row — title on the left, admin nav on the right. Matches the
          layout used on AdminAppsPage for visual consistency between admin
          pages, so the admin dropdown lands in the same spot everywhere. */}
      <div style={styles.pageHeader}>
        <h2 style={{ margin: 0 }}>📏 Estimation Config</h2>
        <AdminMenu />
      </div>
      <p style={styles.subtitle}>
        Tunes the AI estimator used in <em>Jira Issue Manager → Edit Existing Issue</em>.
        Changes take effect on the next estimate (no server restart). Use <strong>Preview prompt</strong> to see exactly what Gemini will read.
      </p>

      {error && <div style={styles.error}>⚠️ {error}</div>}
      {savedAt && !error && (
        <div style={styles.success}>
          ✅ Saved at {savedAt.toLocaleTimeString()} by {config.lastUpdatedBy || 'you'}
        </div>
      )}

      {/* TEAM CAPACITY ----------------------------------------------------- */}
      <section style={styles.section}>
        <h3 style={styles.h3}>Team capacity</h3>
        <div style={styles.row}>
          <label style={styles.label}>
            Story points per dev / week (baseline)
            <input
              type="number" min="0" step="1"
              value={config.team.storyPointsPerWeekPerDev}
              onChange={(e) => updateTeam({ storyPointsPerWeekPerDev: Number(e.target.value) || 0 })}
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Working days per week
            <input
              type="number" min="0" max="7" step="0.5"
              value={config.team.workingDaysPerWeek}
              onChange={(e) => updateTeam({ workingDaysPerWeek: Number(e.target.value) || 0 })}
              style={styles.input}
            />
          </label>
        </div>

        <h4 style={styles.h4}>Named developers (optional — leave empty to use the baseline only)</h4>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Story points / week</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {config.team.developers.map((d, i) => (
              <tr key={i}>
                <td style={styles.td}>
                  <input
                    type="text" value={d.name}
                    onChange={(e) => updateDeveloperRow(i, { name: e.target.value })}
                    style={{ ...styles.input, marginTop: 0 }}
                    placeholder="e.g. Eric"
                  />
                </td>
                <td style={styles.td}>
                  <input
                    type="number" min="0" step="1" value={d.storyPointsPerWeek}
                    onChange={(e) => updateDeveloperRow(i, { storyPointsPerWeek: Number(e.target.value) || 0 })}
                    style={{ ...styles.input, marginTop: 0, width: '120px' }}
                  />
                </td>
                <td style={styles.td}>
                  <button onClick={() => removeDeveloper(i)} style={styles.btnDanger}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addDeveloper} style={styles.btn}>+ Add developer</button>
      </section>

      {/* RULES ------------------------------------------------------------ */}
      <section style={styles.section}>
        <h3 style={styles.h3}>Estimation rules</h3>
        <p style={styles.help}>
          One rule per line. Pasted verbatim into the AI prompt. Examples:
          <br /><code>Anything touching auth flows starts at 3 story points.</code>
          <br /><code>Pure copy changes are 1 SP regardless of file count.</code>
        </p>
        <textarea
          value={(config.rules || []).join('\n')}
          onChange={(e) => updateRulesFromTextarea(e.target.value)}
          rows={Math.max(6, (config.rules || []).length + 1)}
          style={{ ...styles.input, fontFamily: 'monospace', fontSize: '13px' }}
        />
      </section>

      {/* CATEGORY MULTIPLIERS ---------------------------------------------- */}
      <section style={styles.section}>
        <h3 style={styles.h3}>Category multipliers</h3>
        <p style={styles.help}>Guidance the AI considers when the work clearly fits one of these categories. Pure informational — not applied as math.</p>
        <div style={styles.row}>
          {['frontend', 'backend', 'infra', 'data'].map(k => (
            <label key={k} style={styles.label}>
              {k}
              <input
                type="number" min="0.1" step="0.1" value={config.categoryMultipliers?.[k] ?? 1}
                onChange={(e) => updateMultiplier(k, e.target.value)}
                style={styles.input}
              />
            </label>
          ))}
        </div>
      </section>

      {/* CALIBRATION ------------------------------------------------------ */}
      <section style={styles.section}>
        <h3 style={styles.h3}>Calibration examples</h3>
        <p style={styles.help}>
          The strongest accuracy lever. Real closed JIRA tickets where you know both the original estimate and what it actually took.
          Enter the JIRA key (like <code>CP-537</code>), click <strong>Load from JIRA</strong>, then fill in the <em>Actual SP</em> — what it really took, after the fact.
          The AI anchors on these when sizing similar new work and will cite them by key in its rationale.
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>JIRA Key</th>
              <th style={styles.th}>Summary (from JIRA)</th>
              <th style={styles.th}>Est. SP</th>
              <th style={styles.th}>Actual SP</th>
              <th style={styles.th}>Notes</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {(config.calibrationExamples || []).map((e, i) => {
              const state = loaderState[i];
              const isLoading = state === 'loading';
              const errMsg = typeof state === 'string' && state.startsWith('error:') ? state.slice(6) : '';
              return (
                <tr key={i}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <input
                        type="text"
                        value={e.jiraKey || ''}
                        onChange={(ev) => updateCalibrationRow(i, { jiraKey: ev.target.value.toUpperCase() })}
                        onBlur={(ev) => { if (ev.target.value && !e.summary) loadFromJira(i, ev.target.value); }}
                        style={{ ...styles.input, marginTop: 0, width: '110px', fontFamily: 'monospace' }}
                        placeholder="CP-537"
                      />
                      <button
                        type="button"
                        onClick={() => loadFromJira(i, e.jiraKey)}
                        disabled={isLoading || !e.jiraKey}
                        style={{ ...styles.btn, marginTop: 0, fontSize: '0.8rem', padding: '6px 8px' }}
                        title="Fetch summary + story points from JIRA"
                      >
                        {isLoading ? '…' : '↓'}
                      </button>
                    </div>
                    {errMsg && (
                      <div style={{ color: '#ef5350', fontSize: '0.75rem', marginTop: '2px' }}>⚠️ {errMsg}</div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text" value={e.summary || ''}
                      onChange={(ev) => updateCalibrationRow(i, { summary: ev.target.value })}
                      style={{ ...styles.input, marginTop: 0 }}
                      placeholder={e.jiraKey ? '(click ↓ to load)' : 'Will load from JIRA'}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number" min="0" step="1" value={e.estimatedSP}
                      onChange={(ev) => updateCalibrationRow(i, { estimatedSP: Number(ev.target.value) || 0 })}
                      style={{ ...styles.input, marginTop: 0, width: '80px' }}
                      title="Original estimate (auto-loaded from JIRA Story Points)"
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number" min="0" step="1" value={e.actualSP}
                      onChange={(ev) => updateCalibrationRow(i, { actualSP: Number(ev.target.value) || 0 })}
                      style={{ ...styles.input, marginTop: 0, width: '80px' }}
                      title="What it actually took — fill in after the work is done"
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text" value={e.notes || ''}
                      onChange={(ev) => updateCalibrationRow(i, { notes: ev.target.value })}
                      style={{ ...styles.input, marginTop: 0 }}
                      placeholder="What surprised us"
                    />
                  </td>
                  <td style={styles.td}>
                    <button onClick={() => removeCalibration(i)} style={styles.btnDanger}>Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button onClick={addCalibration} style={styles.btn}>+ Add calibration example</button>
      </section>

      {/* ACTIONS ---------------------------------------------------------- */}
      <section style={styles.actions}>
        <button onClick={save} disabled={saving} style={styles.btnPrimary}>
          {saving ? 'Saving…' : '💾 Save'}
        </button>
        <button onClick={fetchConfig} disabled={loading} style={styles.btn}>↻ Reload from disk</button>
        <span style={{ flex: 1 }} />
        <small style={{ color: '#666' }}>
          Last updated: {config.lastUpdatedBy} · {config.lastUpdatedAt}
        </small>
      </section>

      {/* PREVIEW ---------------------------------------------------------- */}
      <section style={styles.section}>
        <h3 style={styles.h3}>Preview prompt (dry run)</h3>
        <p style={styles.help}>
          Renders the exact text Gemini will see for a sample ticket, using the rules + capacity + calibration above. <strong>Save first</strong> if you want the preview to use unsaved edits.
        </p>
        <div style={styles.row}>
          <label style={styles.label}>
            Issue type
            <select
              value={sample.issueType}
              onChange={(e) => setSample(s => ({ ...s, issueType: e.target.value }))}
              style={styles.input}
            >
              {['Story', 'Bug', 'Task', 'Epic'].map(t => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label style={styles.label}>
            Developers
            <select
              value={sample.devCount}
              onChange={(e) => setSample(s => ({ ...s, devCount: Number(e.target.value) }))}
              style={styles.input}
            >
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label style={styles.label}>
            Using AI
            <select
              value={sample.usingAi ? 'yes' : 'no'}
              onChange={(e) => setSample(s => ({ ...s, usingAi: e.target.value === 'yes' }))}
              style={styles.input}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
        </div>
        <label style={styles.label}>
          Sample summary
          <input
            type="text" value={sample.summary}
            onChange={(e) => setSample(s => ({ ...s, summary: e.target.value }))}
            style={styles.input}
          />
        </label>
        <label style={styles.label}>
          Sample description
          <textarea
            value={sample.description}
            onChange={(e) => setSample(s => ({ ...s, description: e.target.value }))}
            rows={4}
            style={{ ...styles.input, fontFamily: 'monospace', fontSize: '13px' }}
          />
        </label>
        <button onClick={runPreview} disabled={previewLoading} style={styles.btnPrimary}>
          {previewLoading ? '…' : '🔍 Preview prompt'}
        </button>
        {previewError && <div style={styles.error}>⚠️ {previewError}</div>}
        {preview && (
          <>
            {preview.capacityContext?.note && (
              <p style={{ ...styles.help, marginTop: '12px' }}>{preview.capacityContext.note}</p>
            )}
            <pre style={styles.pre}>{preview.prompt}</pre>
          </>
        )}
      </section>
    </div>
  );
}

const styles = {
  page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
  pageHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: '12px', marginBottom: '8px',
  },
  subtitle: { color: '#aaa', marginTop: 0 },
  section: {
    marginTop: '20px', padding: '16px', borderRadius: '6px',
    background: '#1f2429', border: '1px solid #2f3a44',
  },
  h3: { marginTop: 0, marginBottom: '8px', color: '#4fc3f7' },
  h4: { marginTop: '16px', marginBottom: '6px', color: '#aaa', fontSize: '0.9rem', textTransform: 'uppercase' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  label: { display: 'flex', flexDirection: 'column', flex: '1 1 180px', minWidth: '140px', color: '#ddd', fontSize: '0.85rem' },
  input: {
    marginTop: '4px', padding: '8px', borderRadius: '4px',
    border: '1px solid #444', background: '#161a1e', color: '#fff',
    width: '100%', boxSizing: 'border-box',
  },
  help: { color: '#888', fontSize: '0.85rem', marginTop: 0 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '8px' },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #2f3a44', color: '#aaa', fontWeight: 600, fontSize: '0.85rem' },
  td: { padding: '4px 8px', verticalAlign: 'top' },
  btn: {
    marginTop: '8px', padding: '8px 14px', background: '#2f3a44',
    color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  btnPrimary: {
    marginTop: '8px', padding: '8px 14px', background: '#0052cc',
    color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600,
  },
  btnDanger: {
    padding: '6px 10px', background: '#5a1a1a',
    color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem',
  },
  actions: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' },
  error: { padding: '8px 12px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: '4px', color: '#ffcdd2', margin: '8px 0' },
  success: { padding: '8px 12px', background: '#1a4a1a', border: '1px solid #4caf50', borderRadius: '4px', color: '#c8e6c9', margin: '8px 0' },
  pre: {
    marginTop: '12px', padding: '12px', background: '#0d1117', border: '1px solid #2f3a44',
    borderRadius: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px',
    color: '#d4d4d4', maxHeight: '500px', overflowY: 'auto',
  },
};
